/**
 * Image Optimization Utilities for Cloudinary
 * Provides URL transformation functions for optimized image loading
 */

const CLOUDINARY_BASE = process.env.REACT_APP_CLOUDINARY_BASE || 'https://res.cloudinary.com/dpt7mjxaj/image/upload';

/**
 * Generate an optimized Cloudinary URL with specified transformations
 * @param {string} imagePath - The image path without the Cloudinary base URL
 * @param {Object} options - Transformation options
 * @returns {string} - Optimized Cloudinary URL
 */
export const getOptimizedImageUrl = (imagePath, options = {}) => {
  const {
    width = null,
    height = null,
    quality = 'auto',
    format = 'auto',
    fetchFormat = 'auto',
    gravity = 'auto',
    crop = 'fill',
    aspectRatio = null,
    dpr = 'auto',
  } = options;

  let transforms = [];

  // Image format optimization
  if (fetchFormat && fetchFormat !== 'auto') {
    transforms.push(`f_${fetchFormat}`);
  } else {
    transforms.push('f_auto');
  }

  // Quality optimization
  if (quality && quality !== 'auto') {
    transforms.push(`q_${quality}`);
  } else {
    transforms.push('q_auto');
  }

  // Width optimization
  if (width) {
    transforms.push(`w_${width}`);
  }

  // Height optimization
  if (height) {
    transforms.push(`h_${height}`);
  }

  // Aspect ratio
  if (aspectRatio) {
    transforms.push(`ar_${aspectRatio}`);
  }

  // Crop mode
  if (crop) {
    transforms.push(`c_${crop}`);
  }

  // Gravity
  if (gravity && gravity !== 'auto') {
    transforms.push(`g_${gravity}`);
  }

  // Device pixel ratio (for retina displays)
  if (dpr && dpr !== 'auto') {
    transforms.push(`dpr_${dpr}`);
  } else {
    transforms.push('dpr_auto');
  }

  // Add progressive compression
  transforms.push('fl_progressive');

  // Build the URL
  const transformString = transforms.join(',');
  const baseUrl = imagePath.startsWith('http') ? '' : CLOUDINARY_BASE;
  const imageUrl = imagePath.startsWith('http') ? imagePath : `${baseUrl}/${imagePath}`;

  // Replace /upload/ with /upload/{transforms}/ if not already done
  if (imageUrl.includes('/upload/') && !imageUrl.includes(`/upload/${transforms[0]}`)) {
    return imageUrl.replace('/upload/', `/upload/${transformString}/`);
  }

  return imageUrl;
};

/**
 * Generate a blurred placeholder URL for progressive image loading
 * @param {string} imagePath - The image path
 * @returns {string} - Blurred placeholder URL
 */
export const getBlurredPlaceholder = (imagePath) => {
  return getOptimizedImageUrl(imagePath, {
    width: 100,
    height: 100,
    quality: 50,
    crop: 'fill',
    fetchFormat: 'auto',
  });
};

/**
 * Generate thumbnail URL with reduced dimensions
 * @param {string} imagePath - The image path
 * @param {number} width - Thumbnail width
 * @returns {string} - Thumbnail URL
 */
export const getThumbnail = (imagePath, width = 400) => {
  return getOptimizedImageUrl(imagePath, {
    width,
    quality: 'auto',
    crop: 'fill',
  });
};

/**
 * Generate responsive image srcset for different screen sizes
 * @param {string} imagePath - The image path
 * @param {Object} options - Options for srcset generation
 * @returns {string} - srcset string for img element
 */
export const getResponsiveImageSrcset = (imagePath, options = {}) => {
  const {
    sizes = [400, 600, 800, 1200, 1600],
    quality = 'auto',
  } = options;

  return sizes
    .map((size) => {
      const url = getOptimizedImageUrl(imagePath, {
        width: size,
        quality,
        crop: 'fill',
      });
      return `${url} ${size}w`;
    })
    .join(', ');
};

/**
 * Generate srcset with DPR variants for high-DPI devices
 * @param {string} imagePath - The image path
 * @param {number} width - Image width
 * @returns {string} - srcset string with DPR variants
 */
export const getHighDPISrcset = (imagePath, width) => {
  return [1, 2, 3]
    .map((dpr) => {
      const url = getOptimizedImageUrl(imagePath, {
        width: Math.round(width / dpr),
        dpr: dpr.toString(),
        quality: 'auto',
      });
      return `${url} ${dpr}x`;
    })
    .join(', ');
};

/**
 * Get image sizes attribute for responsive images
 * @returns {string} - sizes attribute value
 */
export const getImageSizes = () => {
  return '(max-width: 640px) 100vw, (max-width: 1024px) 90vw, (max-width: 1280px) 85vw, 80vw';
};

/**
 * Format all image URLs in a data object
 * @param {Object} data - Data object containing image URLs
 * @param {string} imageKey - Key name for image URLs
 * @param {Object} options - Optimization options
 * @returns {Object} - Data with optimized image URLs
 */
export const formatImagesInData = (data, imageKey = 'image', options = {}) => {
  if (Array.isArray(data)) {
    return data.map((item) => ({
      ...item,
      [imageKey]: item[imageKey] ? getOptimizedImageUrl(item[imageKey], options) : item[imageKey],
    }));
  }

  return {
    ...data,
    [imageKey]: data[imageKey] ? getOptimizedImageUrl(data[imageKey], options) : data[imageKey],
  };
};

export default {
  getOptimizedImageUrl,
  getBlurredPlaceholder,
  getThumbnail,
  getResponsiveImageSrcset,
  getHighDPISrcset,
  getImageSizes,
  formatImagesInData,
};
